<template>
    <div
        class="checkout py-4 px-2 px-md-4"
        v-if="!(shared.token && !shared.user)"
    >
        <div class="py-4" v-if="empty">
            <h4>You haven't added anything to your cart</h4>
        </div>
        <template v-else>
            <checkout-offer />
            <div class="px-2 px-md-4 mt-5 bg-white text-dark">
                <h4>
                    Do you have an iPhone or iPad?
                </h4>
                <div class="mb-5">
                    <v-button
                        class="mr-2"
                        @click="iOS = true"
                        :variant="yesButtonVariant"
                        >Yes</v-button
                    >
                    <v-button
                        :variant="noButtonVariant"
                        @click.prevent="
                            iOS = false;
                            showMailingListModal = true;
                        "
                        >No</v-button
                    >
                    <android-modal
                        :show.sync="showMailingListModal"
                        class="text-left"
                    />
                </div>
                <template v-if="iOS">
                    <template v-if="isSignIn">
                        <p><a href="javascript:void(0);" @click="isSignIn = false">Need to create an account?</a></p>
                        <h4>Sign in</h4>
                        <v-form-sign-in ref="signIn" />
                        <v-flex align="center" class="mt-3" justify="end">
                            <v-button :disabled="saving" @click.prevent="signIn" block variant="primary">Sign in</v-button>
                        </v-flex>
                    </template>
                    <template v-else>
                        <template v-if="shared.user">
                            <h4>Your Account</h4>
                            You're logged in as {{ shared.user.account.full_name }}. If this is not you or you'd like to make a new account, <a :href="$route.path" @click.prevent="signOut">sign out</a>.
                        </template>
                        <template v-else>
                            <p><a href="javascript:void(0);" @click="isSignIn = true">Already have an account?</a></p>
                            <h4>Create your account</h4>
                            <v-form-sign-up no-footer ref="signUp" />
                        </template>
                        <template v-if="total">
                            <h4 class="mt-5">Enter your payment details</h4>
                            <v-stripe-token-form no-footer ref="token" />
                        </template>
                        <div class="mt-5">
                            <v-button
                                :disabled="saving"
                                @click="save"
                                block
                                class="w-100"
                                size="lg"
                                variant="primary"
                                >Sign up</v-button
                            >
                        </div>
                    </template>
                </template>
            </div>
        </template>

        <v-modal :cancel="false" :show="!!error" @closed="error = null" ok="Ok">
            <div class="p-4">
                <h4>Oh no!</h4>
                {{ error }}
            </div>
        </v-modal>
    </div>
</template>

<script>
import VFormSignUp from "@/components/ui/VFormSignUp";
import VFormSignIn from "@/components/ui/VFormSignIn";
import VStripeTokenForm from "@/components/ui/VStripeTokenForm";
import VModal from "@/components/ui/VModal";
import CheckoutOffer from "@/components/CheckoutOffer";
import store from "@/store";
import AndroidModal from "@/components/AndroidModal";
import { platform } from "@/utils/platform";

export default {
    name: "CheckoutCartView",
    components: {
        VFormSignUp,
        VFormSignIn,
        VStripeTokenForm,
        VModal,
        CheckoutOffer,
        AndroidModal
    },
    data() {
        return {
            shared: store.state,
            lastRoute: null,
            saving: false,
            error: null,
            showMailingListModal: false,
            iOS: platform.iOS || null, // If we detect they are on iOS, set this to true
            isSignIn: false
        };
    },
    computed: {
        empty() {
            return !this.shared.cart.length && !this.shared.offer;
        },
        total() {
            if (this.shared.offer) {
                return parseFloat(this.shared.offer.price) || 0;
            }
            return 0;
        },
        yesButtonVariant() {
            return this.iOS === true ? "success" : "outline-secondary";
        },
        noButtonVariant() {
            return this.iOS === false ? "success" : "outline-secondary";
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.lastRoute = from;
        });
    },
    methods: {
        signIn() {
            if (!this.saving) {
                this.saving = true;
                if (this.shared.user)
                    return Promise.resolve(this.shared.user);
                return this.$refs.signIn
                    .signIn()
                    .catch(() => Promise.reject("signIn"))
                    .finally(() => {
                        this.saving = false;
                        if (this.shared.user)
                            this.isSignIn = false;
                    });
            }
        },
        save() {
            if (!this.saving) {
                this.saving = true;

                // Chain each step of the sign up flow, staring with sign up
                this.signUp()
                    .then(this.accept)
                    .then(this.token)
                    .then(this.success)
                    .catch(e => {
                        // TODO: Do something with the errors on each step
                        if (process.env.NODE_ENV != "production") {
                            console.log(e);
                        }
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            }
        },
        signUp() {
            // If there is a logged in user return a resolved promise
            if (this.shared.user) {
                return Promise.resolve(this.shared.user);
            }
            return this.$refs.signUp
                .save()
                .catch(() => Promise.reject("signUp"));
        },
        accept() {
            // Accept the offer in our backend
            // TODO: Update to new offer backend when ready
            return this.$ladder
                .create("me/offers/" + this.shared.offer.token)
                .catch(e => {
                    this.error = e.message;
                    return Promise.reject("offer");
                });
        },
        async token() {

            // Free offers do not require a credit card and the membership is immediately started in the backend.
            if (!this.total)
                return Promise.resolve();

            // Get the offer token.
            let token = await this.$refs.token.save();

            // Start the membership.
            try {
                await this.$ladder.create('me/membership', {
                    token: token.id
                });
            } catch (e) {
                this.error = e.message;
                this.$emit('error', e);
                return Promise.reject(e);
            }
        },
        success() {
            // Track a successful completion of a signup / order
            if (window.analytics) {
                try {
                    window.analytics.track("Order Completed", {
                        affiliation: "Ladder",
                        total: this.total,
                        revenue: this.total,
                        shipping: 0,
                        tax: 0,
                        discount: 0,
                        currency: "USD",
                        products: [
                            {
                                product_id: this.shared.offer.id,
                                sku: this.shared.offer.token,
                                name: this.shared.offer.full_name,
                                price: this.shared.offer.price,
                                url: `https://joinladder.com/offer/${this.shared.offer.token}`
                            }
                        ]
                    });
                } catch (e) {
                    // Fail silently
                }
            }

            this.$router.push("/checkout/success");
        },
        async signOut() {
            await this.$ladder.signOut();
            window.location.reload(true);
        }
    }
};
</script>

<style scoped lang="scss">
.checkout {
    h4 {
        font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
            "Courier New", monospace;
        margin-bottom: 1rem;
    }
}
</style>
